import CardActions from "@/components/game/CardActions";
import { useEffect } from "react";
import GameCard from "@/components/game/GameCard";
import { CARD_RATIO, printedName } from "@/lib/artAssets";
import { FullStats } from "@/components/game/CardStats";
import { cardTypeLabel } from "@/components/game/CardArt";
import Coin from "@/components/game/Coin";

const W = `min(400px, calc(90 * var(--svw)), calc((100 * var(--svh) - 140px) / ${CARD_RATIO}))`;

// Card text + live stats (house-rule values) beside the art.
function CardInfo({ card }) {
  const lines = (card.placement_text || "").split("\n").filter(Boolean);
  return (
    <div
      data-testid="card-zoom-info"
      className="flex flex-col gap-3 p-4 shrink-0"
      style={{ width: "min(320px, calc(90 * var(--svw)))", background: "var(--card)", borderRadius: "var(--radius-card)", boxShadow: "var(--shadow-card)", color: "var(--ink)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div data-testid="card-zoom-type" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-4)" }}>{cardTypeLabel(card)}</div>
          <div data-testid="card-zoom-name" style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{printedName(card)}</div>
        </div>
        <span data-testid="card-zoom-cost" className="shrink-0 inline-flex items-center gap-1" style={{ fontSize: 15, fontWeight: 800, color: "var(--sea)" }}>
          <Coin size={16} /> {card.cost}
        </span>
      </div>
      {card.kind === "build" && (
        <div style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "1px solid var(--line-soft)", padding: "10px 0" }}>
          <FullStats card={card} testid="card-zoom-stats" />
        </div>
      )}
      {card.description && (
        <p data-testid="card-zoom-description" style={{ fontSize: 13, lineHeight: 1.45, color: "var(--ink-3)" }}>{card.description}</p>
      )}
      {lines.length > 0 && (
        <div data-testid="card-zoom-placement" className="flex flex-col gap-1" style={{ fontSize: 12.5, lineHeight: 1.4, fontWeight: 700, background: "var(--line-soft)", borderRadius: 8, padding: "8px 10px" }}>
          {lines.map((l) => <span key={l}>{l}</span>)}
        </div>
      )}
    </div>
  );
}

// Full-size card view + card info; click anywhere (or Esc) closes. Optional action button below.
// actions: the same buttons as under the selected card (CardActions), when this card is the one shown there.
export default function CardZoom({ zoom, onClose, actions = null }) {
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, onClose]);
  if (!zoom) return null;
  return (
    <div
      data-testid="card-zoom-overlay"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-4 p-4 overflow-y-auto animate-in fade-in duration-150"
      style={{ background: "rgba(42,46,44,0.45)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div className="flex flex-col min-[900px]:flex-row items-center justify-center gap-4 max-w-full">
        <div
          className="animate-in zoom-in-95 duration-200 shrink-0"
          style={{ width: W, height: `calc(${W} * ${CARD_RATIO})` }}
          data-testid="card-zoom-card-wrap"
        >
          <GameCard card={zoom.card} size="fit" onClick={onClose} testid="card-zoom-card" />
        </div>
        <CardInfo card={zoom.card} />
      </div>
      {zoom.note && (
        <div data-testid="card-zoom-note" className="max-w-[400px] text-center text-sm font-bold px-3 py-1.5 rounded-md" style={{ background: "var(--card)", color: "var(--ink)" }}>
          {zoom.note}
        </div>
      )}
      {actions?.length > 0 && (
        <div data-testid="card-zoom-actions" className="w-full" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
          <CardActions large testid="card-zoom-buttons" buttons={actions.map((b) => ({ ...b, onClick: () => { b.onClick?.(); onClose(); } }))} />
        </div>
      )}
      {zoom.actionLabel && (
        <button
          type="button"
          data-testid="card-zoom-action"
          className="btn-tactile btn-tactile--primary"
          onClick={(e) => {
            e.stopPropagation();
            zoom.onAction();
            onClose();
          }}
        >
          {zoom.actionLabel}
        </button>
      )}
    </div>
  );
}
